import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { Download, FileCheck2, Mail, Printer } from "lucide-react";
import type { PreviewResponse } from "../../types/preview";
import { printDocument } from "../../lib/browser";
import { CopyButton } from "../shared/CopyButton";
import { CopyChannelsButton } from "./CopyChannelsButton";

export function ReleasedHeader({ protocolo }: { protocolo: string }) {
  return (
    <div data-testid="released-header" className="fade-up mx-auto mb-6 flex w-full max-w-[794px] flex-col gap-4 rounded-md border border-emerald-500/40 bg-slate-900 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
      <div className="flex items-start gap-3">
        <FileCheck2 aria-hidden="true" className="mt-0.5 h-6 w-6 shrink-0 text-emerald-400" />
        <div>
          <p className="font-semibold text-slate-100">Documento liberado</p>
          <p data-testid="pdf-hint" className="mt-1 flex items-center gap-1.5 text-xs text-slate-400">
            <Printer aria-hidden="true" className="h-3.5 w-3.5" />Na janela de impressão, escolha &quot;Salvar como PDF&quot;.
          </p>
        </div>
      </div>
      <button type="button" onClick={() => printDocument(protocolo)} data-testid="download-pdf-button" className="btn-primary">
        <Download aria-hidden="true" className="h-4 w-4" />Baixar Notificação (PDF)
      </button>
    </div>
  );
}

const STEPS = ["Baixe o PDF", "Envie para os e-mails acima com cópia para seu e-mail", "Guarde o comprovante de envio"];

export function ReleaseOrientation({ preview }: { preview: PreviewResponse }) {
  return (
    <section data-testid="release-orientation" className="fade-up mx-auto mt-8 w-full max-w-[794px] rounded-md border border-slate-800 bg-slate-900 p-6 sm:p-8">
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-emerald-500">Roteiro de protocolo</p>
      <h3 className="mt-2 text-lg font-semibold text-slate-100">Como enviar sua notificação</h3>
      {preview.emails.length > 0 ? (
        <>
        <div className="mt-6 flex justify-end"><CopyChannelsButton emails={preview.emails} /></div>
        <ul className="mt-3 space-y-3">
          {preview.emails.map((e, i) => (
            <li key={e.email} data-testid={`release-email-${i}`} className="flex flex-col gap-3 rounded-md border border-slate-800 bg-slate-950 p-3 sm:flex-row sm:items-center sm:justify-between">
              <span className="flex min-w-0 items-center gap-2 text-sm text-slate-200">
                <Mail aria-hidden="true" className="h-4 w-4 shrink-0 text-slate-500" />
                <span className="break-all font-mono">{e.email}</span>
                <span data-testid={`release-email-${i}-tag`} className={`shrink-0 rounded-sm px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${e.verificado ? "bg-emerald-500/10 text-emerald-400" : "bg-amber-500/10 text-amber-300"}`}>
                  {e.verificado ? "verificado" : "a verificar"}
                </span>
              </span>
              <CopyButton text={e.email} label="Copiar" testId={`copy-email-${i}`} compact />
            </li>
          ))}
        </ul>
        </>
      ) : (
        <p data-testid="release-no-emails" className="mt-4 text-sm text-slate-400">Nenhum e-mail de destino verificado para esta empresa. Consulte o contrato ou o site institucional da incorporadora.</p>
      )}
      <ol data-testid="release-steps" className="mt-8 grid gap-3 sm:grid-cols-3">
        {STEPS.map((s, i) => (
          <li key={s} className="flex gap-3 rounded-md border border-slate-800 p-4 text-sm text-slate-300">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-emerald-500 font-mono text-xs font-semibold text-slate-950">{i + 1}</span>{s}
          </li>
        ))}
      </ol>
    </section>
  );
}

export function PrintPortal({ children }: { children: ReactNode }) {
  return createPortal(<div id="print-root" data-testid="print-root">{children}</div>, document.body);
}
